import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './HomeStudent.css';
import novasLogo from '../../assets/novas-logo.png';
import { getLessonListForStudent } from '../../utils/lessonsService';
import { getScheduleForStudent } from '../../utils/scheduleService';
import { getHomework } from '../../utils/homeworkService';
import { getDictionary } from '../../utils/dictionaryService';
import { convertTimeRange, lessonStatusFromZones } from '../../utils/timezone';
import { usePaymentBalance } from '../../hooks/usePaymentBalance';

export default function HomeStudent({ studentData, onLessonClick, onGoToHomework, onGoToDictionary }) {
  const [teacherSchedule, setTeacherSchedule] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [nextLesson, setNextLesson] = useState(null);
  const [activeSectionTab, setActiveSectionTab] = useState('Lessons');
  const [lessonSearchTerm, setLessonSearchTerm] = useState('');

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [schedule, setSchedule] = useState([]);
  const [allSchedule, setAllSchedule] = useState([]);
  const [payToast, setPayToast] = useState(null);

  // Scoped per student so learned-word state doesn't leak between different
  // student accounts logged in on the same browser.
  const getLearnedWordsKey = useCallback(
    () => (studentData?.id ? `student-learned-words_${studentData.id}` : null),
    [studentData?.id]
  );

  // Same reconciled balance StudentProfile computes for the teacher — see
  // usePaymentBalance for why this needs to run here too, not just there.
  const { paymentBalance, paymentCurrency } = usePaymentBalance({
    studentId: studentData?.slug,
    studentSupabaseId: studentData?.id,
    studentData,
    lessons,
    scheduleItems: allSchedule,
    ready: !lessonsLoading,
  });

  const [homeworkSummary, setHomeworkSummary] = useState({ new: 0, toReview: 0 });
  const [homeworkLoading, setHomeworkLoading] = useState(true);
  const [dictionaryRecent, setDictionaryRecent] = useState([]);
  const [dictionaryLoading, setDictionaryLoading] = useState(true);
  const [dictionaryDeck, setDictionaryDeck] = useState([]);
  const [flippedCardId, setFlippedCardId] = useState(null); // id карточки, у которой показываем оборот (null = лицевая)
  const [deckMotion, setDeckMotion] = useState(null); // 'know' | 'dont' | null
  const [deckBusy, setDeckBusy] = useState(false);
  const [deckPage, setDeckPage] = useState(0);
  const lastDeckTapRef = useRef(0); // защита от двойного срабатывания (touch + click)

  const DICTIONARY_DECK_PAGE_SIZE = 15;
  const [selectedWeekDayIndex, setSelectedWeekDayIndex] = useState(null);

  // Загружаем расписание для студента из Supabase
  useEffect(() => {
    const loadSchedule = async () => {
      if (!studentData?.id) {
        console.warn('⚠️ HomeStudent - No studentData.id, cannot load schedule');
        return;
      }

      try {
        console.log('📅 HomeStudent - Loading schedule for student:', {
          studentId: studentData.id,
          studentName: studentData.name
        });
        
        const studentSchedule = await getScheduleForStudent(studentData.id);
        
        console.log('📅 HomeStudent - Loaded schedule from Supabase:', {
          count: studentSchedule?.length || 0,
          schedule: studentSchedule
        });
        
        // Фильтруем по текущей неделе (Пн–Вс): и прошедшие, и будущие уроки
        const now = new Date();
        const dayOfWeek = now.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset, 0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        const weekStartMs = weekStart.getTime();
        const weekEndMs = weekEnd.getTime();
        const thisWeekSchedule = (studentSchedule || []).filter(item => {
          if (!item.date) return false;
          const itemDate = new Date(item.date);
          const ms = itemDate.getTime();
          return ms >= weekStartMs && ms <= weekEndMs;
        });
        setSchedule(thisWeekSchedule);
        // Unfiltered — the payment-balance reconciliation needs every
        // lesson (including past ones outside this week) to know which
        // 'done' lessons already have an auto-charge.
        setAllSchedule(studentSchedule || []);
      } catch (error) {
        console.error('❌ HomeStudent - Error loading schedule:', error);
        setSchedule([]);
        setAllSchedule([]);
      }
    };

    loadSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData?.id]);

  // Homework summary for widget
  useEffect(() => {
    const loadHomework = async () => {
      if (!studentData?.id) {
        setHomeworkLoading(false);
        return;
      }
      try {
        setHomeworkLoading(true);
        const list = await getHomework(studentData.id);
        const newCount = (list || []).filter(h => h.status === 'assigned').length;
        const toReviewCount = (list || []).filter(h => h.status === 'submitted').length;
        setHomeworkSummary({ new: newCount, toReview: toReviewCount });
      } catch (e) {
        console.error('HomeStudent - Error loading homework:', e);
        setHomeworkSummary({ new: 0, toReview: 0 });
      } finally {
        setHomeworkLoading(false);
      }
    };
    loadHomework();
    const onUpdate = () => loadHomework();
    window.addEventListener('homeworkUpdated', onUpdate);
    return () => window.removeEventListener('homeworkUpdated', onUpdate);
  }, [studentData?.id]);

  // Recent dictionary entries (last 5)
  useEffect(() => {
    const loadDictionaryInitial = async () => {
      if (!studentData?.id) {
        setDictionaryLoading(false);
        return;
      }
      try {
        setDictionaryLoading(true);
        const entries = await getDictionary(studentData.id);
        setDictionaryRecent(entries || []);
      } catch (e) {
        console.error('HomeStudent - Error loading dictionary:', e);
        setDictionaryRecent([]);
      } finally {
        setDictionaryLoading(false);
      }
    };
    const loadDictionarySilent = async () => {
      if (!studentData?.id) return;
      try {
        const entries = await getDictionary(studentData.id);
        setDictionaryRecent(entries || []);
      } catch (e) {
        // keep previous UI, avoid flicker
        console.warn('HomeStudent - Silent dictionary refresh failed:', e);
      }
    };

    loadDictionaryInitial();
    const onUpdate = () => loadDictionarySilent();
    window.addEventListener('dictionaryUpdated', onUpdate);
    return () => window.removeEventListener('dictionaryUpdated', onUpdate);
  }, [studentData?.id]);

  // Build flashcard deck from recent dictionary entries, excluding "learned"
  useEffect(() => {
    try {
      const learnedWordsKey = getLearnedWordsKey();
      const learnedRaw = learnedWordsKey ? localStorage.getItem(learnedWordsKey) : null;
      const learnedIds = new Set((learnedRaw ? JSON.parse(learnedRaw) : []) || []);
      const candidates = (dictionaryRecent || []).filter(e => e?.id && !learnedIds.has(e.id));
      const start = deckPage * DICTIONARY_DECK_PAGE_SIZE;
      const deck = candidates.slice(start, start + DICTIONARY_DECK_PAGE_SIZE);
      const newTopId = deck[0]?.id;
      setDictionaryDeck(deck);
      setFlippedCardId(prev => (newTopId && prev === newTopId ? prev : null));
    } catch (e) {
      const fallback = (dictionaryRecent || []).slice(0, DICTIONARY_DECK_PAGE_SIZE);
      const newTopId = fallback[0]?.id;
      setDictionaryDeck(fallback);
      setFlippedCardId(prev => (newTopId && prev === newTopId ? prev : null));
    }
  }, [dictionaryRecent, deckPage, getLearnedWordsKey]);

  const resetLearnedWords = () => {
    try {
      const learnedWordsKey = getLearnedWordsKey();
      if (learnedWordsKey) localStorage.removeItem(learnedWordsKey);
    } catch (e) {
      // ignore
    }
    window.dispatchEvent(new CustomEvent('dictionaryUpdated'));
    setDeckPage(0);
    setFlippedCardId(null);
  };

  const loadMoreDeckWords = () => {
    setDeckPage(p => p + 1);
    setFlippedCardId(null);
  };

  const markWordLearned = (entryId) => {
    if (!entryId) return;
    try {
      const learnedWordsKey = getLearnedWordsKey();
      const learnedRaw = learnedWordsKey ? localStorage.getItem(learnedWordsKey) : null;
      const learned = (learnedRaw ? JSON.parse(learnedRaw) : []) || [];
      if (!learned.includes(entryId)) {
        learned.push(entryId);
        if (learnedWordsKey) localStorage.setItem(learnedWordsKey, JSON.stringify(learned));
      }
    } catch (e) {
      // ignore
    }
    // Let Dictionary page refresh stats without forcing a loading state here
    window.dispatchEvent(new CustomEvent('dictionaryUpdated'));
  };

  const handleDeckKnow = (e) => {
    e?.stopPropagation?.();
    if (deckBusy) return;
    const top = dictionaryDeck?.[0];
    if (!top?.id) return;
    setDeckBusy(true);
    setDeckMotion('know');
    window.setTimeout(() => {
      markWordLearned(top.id);
      setDictionaryDeck(prev => (prev || []).slice(1));
      setFlippedCardId(null);
      setDeckMotion(null);
      setDeckBusy(false);
    }, 260);
  };

  const handleDeckDontKnow = (e) => {
    e?.stopPropagation?.();
    if (deckBusy) return;
    setDeckBusy(true);
    setDeckMotion('dont');
    window.setTimeout(() => {
      setDictionaryDeck(prev => {
        const list = prev || [];
        if (list.length <= 1) return list;
        const [first, ...rest] = list;
        return [...rest, first]; // keep in deck, move to end
      });
      setFlippedCardId(null);
      setDeckMotion(null);
      setDeckBusy(false);
    }, 220);
  };

  const speakWord = (word) => {
    try {
      if (!word) return;
      if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
        console.warn('Speech synthesis not supported in this browser.');
        return;
      }
      // Cancel any ongoing utterance so repeated taps work reliably.
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(String(word));
      u.lang = 'en-US';
      u.rate = 0.95;
      u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch (err) {
      console.warn('Failed to speak word:', err);
    }
  };

  // Загружаем уроки для студента из Supabase
  useEffect(() => {
    const loadLessons = async () => {
      if (!studentData?.id) {
        console.warn('⚠️ HomeStudent - No studentData.id, cannot load lessons');
        setLessonsLoading(false);
        return;
      }

      try {
        setLessonsLoading(true);
        console.log('📚 HomeStudent - Loading lessons for student:', {
          studentId: studentData.id,
          studentName: studentData.name,
          studentEmail: studentData.email,
          studentSlug: studentData.slug
        });
        
        const studentLessons = await getLessonListForStudent(studentData.id);
        
        console.log('📚 HomeStudent - Loaded lessons from Supabase:', {
          count: studentLessons?.length || 0,
          lessons: studentLessons,
          studentId: studentData.id
        });
        
        if (!studentLessons || studentLessons.length === 0) {
          console.warn('⚠️ HomeStudent - No lessons returned from getLessonListForStudent');
        } else {
          console.log('📚 HomeStudent - First lesson details:', {
            id: studentLessons[0].id,
            title: studentLessons[0].title,
            studentId: studentLessons[0].studentId,
            hasTitle: !!studentLessons[0].title
          });
        }
        
        // Проверим каждый урок
        if (studentLessons && studentLessons.length > 0) {
          studentLessons.forEach((lesson, index) => {
            console.log(`📚 Lesson ${index + 1}:`, {
              id: lesson.id,
              title: lesson.title,
              date: lesson.date,
              timeFrom: lesson.timeFrom || lesson.from,
              timeTo: lesson.timeTo || lesson.to,
              studentId: lesson.studentId,
              status: lesson.status
            });
          });
        } else {
          console.warn('⚠️ HomeStudent - No lessons found for student:', studentData.id);
        }
        
        setLessons(studentLessons || []);
      } catch (error) {
        console.error('❌ HomeStudent - Error loading lessons:', error);
        setLessons([]);
      } finally {
        setLessonsLoading(false);
      }
    };

    loadLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData?.id]);

  // Filter lessons: show all lessons assigned to student
  // Students should see all their lessons, even if they don't have content yet
  const lessonsWithContent = useMemo(() => {
    console.log('📚 HomeStudent - Filtering lessons:', {
      totalLessons: lessons.length,
      lessons: lessons.map(l => ({
        id: l.id,
        title: l.title,
        hasContainers: !!l.containers?.length,
        hasDocContainers: !!l.docContainers?.length,
        hasBanner: !!l.bannerImage
      }))
    });
    
    // For students, show all lessons that have at least a title
    // This includes both full lessons and scheduled lessons
    const filtered = lessons.filter(lesson => {
      // Only filter out completely empty lessons (no title)
      return lesson.title && lesson.title.trim() !== '';
    });
    
    console.log('📚 HomeStudent - Filtered lessons:', {
      filteredCount: filtered.length,
      filtered: filtered.map(l => ({ id: l.id, title: l.title }))
    });
    
    return filtered;
  }, [lessons]);

  // Filter lessons based on search term (only lessons with content)
  const filteredLessons = useMemo(() => {
    const base = !lessonSearchTerm.trim()
      ? lessonsWithContent
      : lessonsWithContent.filter(lesson =>
          lesson.title?.toLowerCase().includes(lessonSearchTerm.toLowerCase()) ||
          lesson.date?.toLowerCase().includes(lessonSearchTerm.toLowerCase())
        );
    // In-progress lessons are the most relevant right now — show them
    // first (stable sort keeps everything else in its existing order).
    return [...base].sort((a, b) => {
      const priority = (status) => (status === 'in_progress' ? 0 : 1);
      return priority(a.status) - priority(b.status);
    });
  }, [lessonsWithContent, lessonSearchTerm]);

  // Check if lesson can be entered (10 minutes before start)
  const canEnterLesson = (lesson) => {
    if (!lesson.date || !lesson.from) return false;
    
    const lessonDateTime = new Date(`${lesson.date} ${lesson.from}`);
    const now = new Date();
    const tenMinutesBefore = new Date(lessonDateTime.getTime() - 10 * 60 * 1000);
    
    return now >= tenMinutesBefore && now <= lessonDateTime;
  };

  // Format lesson time for display
  const formatLessonTime = (lesson) => {
    if (!lesson.date || !lesson.from) return 'TBD';
    
    const lessonDate = new Date(`${lesson.date} ${lesson.from}`);
    const now = new Date();
    const timeDiff = lessonDate.getTime() - now.getTime();
    
    if (timeDiff < 0) {
      return 'Past';
    } else if (timeDiff <= 10 * 60 * 1000) { // 10 minutes
      return 'Starting soon';
    } else {
      return `${lesson.from}`;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getPlanetForLevel = (level) => {
    const levelMap = {
      'C2': 'Sun',
      'C1+': 'Mercury', 
      'C1': 'Venus',
      'B2+': 'Earth',
      'B2': 'Mars',
      'B1+': 'Jupiter',
      'B1': 'Saturn',
      'A2': 'Uranus',
      'A1': 'Neptune'
    };
    return levelMap[level] || 'Earth';
  };

  // eslint-disable-next-line no-unused-vars
  const getProgressPercentage = (level) => {
    const levelProgress = {
      'C2': 100,
      'C1+': 88,
      'C1': 77,
      'B2+': 66,
      'B2': 55,
      'B1+': 44,
      'B1': 33,
      'A2': 22,
      'A1': 11
    };
    return levelProgress[level] || 33;
  };


  // Get upcoming teacher schedule data from schedule table (not from lessons)
  const getTeacherSchedule = () => {
    try {
      console.log('📅 getTeacherSchedule - schedule state:', schedule);
      console.log('📅 getTeacherSchedule - schedule length:', schedule?.length);
      
      if (!schedule || schedule.length === 0) {
        console.log('⚠️ getTeacherSchedule - No schedule available');
        return [];
      }
      
      // Schedule уже загружен с фильтром по будущим датам из getScheduleForStudent
      // Просто сортируем по дате и времени
      const sorted = [...schedule].sort((a, b) => {
        if (!a.date || !b.date) return 0;
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
        }
        // Если даты одинаковые, сортируем по времени
        const timeA = a.from || a.timeFrom || '00:00';
        const timeB = b.from || b.timeFrom || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      console.log('✅ getTeacherSchedule - Sorted schedule:', sorted);
      return sorted;
    } catch (error) {
      console.error('❌ Error loading teacher schedule:', error);
      return [];
    }
  };

  // Get teacher contact information from studentData
  const getTeacherContactInfo = () => {
    try {
      console.log('getTeacherContactInfo - studentData:', studentData);
      console.log('getTeacherContactInfo - studentData.teacher:', studentData?.teacher);
      console.log('getTeacherContactInfo - studentData.teacherName:', studentData?.teacherName);
      
      // Получаем данные преподавателя из studentData (загружаются из Supabase)
      if (studentData?.teacher) {
        // Убеждаемся, что используем именно данные преподавателя, а не студента
        const teacherName = studentData.teacher.name;
        // Проверяем, что это не имя студента (если teacher.name совпадает с studentData.name, это ошибка)
        if (teacherName && teacherName !== studentData?.name && teacherName !== 'NOVAS') {
          console.log('Using teacher data from studentData.teacher:', studentData.teacher);
          return {
            name: teacherName,
            email: studentData.teacher.email || '',
            phone: studentData.teacher.phone || '',
            photo: studentData.teacher.photo || null
          };
        } else if (teacherName && teacherName === studentData?.name) {
          console.error('ERROR: Teacher name matches student name! This should not happen.', {
            teacherName,
            studentName: studentData?.name,
            teacher: studentData.teacher
          });
        }
      }
      
      // Fallback: если teacher не загружен, используем teacherName
      if (studentData?.teacherName && studentData.teacherName !== 'NOVAS') {
        console.log('Using teacherName as fallback:', studentData.teacherName);
        return {
          name: studentData.teacherName,
          email: studentData.teacher?.email || '',
          phone: studentData.teacher?.phone || '',
          photo: studentData.teacher?.photo || null
        };
      }
      
      // Если teacher есть, но name = 'NOVAS', все равно используем его данные
      if (studentData?.teacher) {
        const teacherName = studentData.teacher.name;
        // Проверяем, что это не имя студента
        if (teacherName && teacherName !== studentData?.name) {
          console.log('Using teacher data even if name is NOVAS:', studentData.teacher);
          return {
            name: teacherName || 'NOVAS',
            email: studentData.teacher.email || '',
            phone: studentData.teacher.phone || '',
            photo: studentData.teacher.photo || null
          };
        } else if (teacherName === studentData?.name) {
          console.error('ERROR: Teacher name matches student name! Using NOVAS fallback.', {
            teacherName,
            studentName: studentData?.name
          });
        }
      }
      
      // Последний fallback
      console.warn('No teacher data found, using default NOVAS');
      return {
        name: 'NOVAS',
        email: '',
        phone: '',
        photo: null
      };
    } catch (error) {
      console.error('Error getting teacher contact info:', error);
      return {
        name: 'NOVAS',
        email: '',
        phone: '',
        photo: null
      };
    }
  };

  // Format time to remove seconds
  const formatTime = (timeString) => {
    if (!timeString) return '';
    if (timeString.includes(':') && timeString.split(':').length === 3) {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    }
    return timeString;
  };

  // Format lesson date and time for display
  const formatLessonDateTime = (lesson) => {
    if (!lesson.date) {
      console.warn('⚠️ formatLessonDateTime - No date in lesson:', lesson);
      return { date: 'TBD', dayOfWeek: '', time: 'TBD' };
    }
    
    try {
      let lessonDate;
      
      // Handle different date formats
      if (typeof lesson.date === 'string') {
        if (lesson.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // YYYY-MM-DD format
          lessonDate = new Date(lesson.date + 'T00:00:00');
        } else if (lesson.date.includes('T')) {
          // ISO format with time
          lessonDate = new Date(lesson.date);
        } else {
          // Try to parse as-is
          lessonDate = new Date(lesson.date);
        }
      } else if (lesson.date instanceof Date) {
        lessonDate = lesson.date;
      } else {
        console.warn('⚠️ formatLessonDateTime - Unknown date format:', lesson.date);
        return { date: 'TBD', dayOfWeek: '', time: 'TBD' };
      }
      
      if (isNaN(lessonDate.getTime())) {
        console.warn('⚠️ formatLessonDateTime - Invalid date:', lesson.date);
        return { date: 'TBD', dayOfWeek: '', time: 'TBD' };
      }
      
      // Format date
      const dateStr = lessonDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      
      // Format day of week
      const dayOfWeek = lessonDate.toLocaleDateString('en-US', { 
        weekday: 'short' 
      });
      
      // Format time range - check both 'from' and 'timeFrom', 'to' and 'timeTo'
      let timeStr = 'TBD';
      const from = lesson.timeFrom || lesson.from;
      const to = lesson.timeTo || lesson.to;
      
      if (from && to) {
        timeStr = `${formatTime(from)} - ${formatTime(to)}`;
      } else if (from) {
        timeStr = formatTime(from);
      }
      
      return {
        date: dateStr,
        dayOfWeek: dayOfWeek,
        time: timeStr
      };
    } catch (error) {
      console.error('❌ Error formatting lesson date:', error, lesson);
      return { date: 'TBD', dayOfWeek: '', time: 'TBD' };
    }
  };

  const formatScheduleCardDate = (item) => {
    if (!item?.date) return '—';
    const d = typeof item.date === 'string' && item.date.match(/^\d{4}-\d{2}-\d{2}$/)
      ? new Date(item.date + 'T00:00:00')
      : new Date(item.date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getLessonStatus = (item) => {
    const srcTz = studentData?.teacher?.timezone || null;
    return lessonStatusFromZones({
      date: item?.date,
      from: item?.timeFrom || item?.from,
      to: item?.timeTo || item?.to
    }, srcTz);
  };

  const getNextLesson = () => {
    try {
      // Используем расписание, а не уроки
      const scheduleItems = getTeacherSchedule();
      if (!scheduleItems || scheduleItems.length === 0) return null;
      
      // Первый элемент уже отсортирован по дате и времени
      const nextLesson = scheduleItems[0];
      if (!nextLesson || !nextLesson.date) return null;
      
      let lessonDate;
      if (typeof nextLesson.date === 'string') {
        if (nextLesson.date.includes('T') || nextLesson.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          lessonDate = new Date(nextLesson.date);
        } else {
          // toDateString() format
          lessonDate = new Date(nextLesson.date);
        }
      } else if (nextLesson.date instanceof Date) {
        lessonDate = nextLesson.date;
      } else {
        return null;
      }
        
        let dateText = '';
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        
      const lessonDateOnly = new Date(lessonDate.getFullYear(), lessonDate.getMonth(), lessonDate.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
      
      if (lessonDateOnly.getTime() === todayOnly.getTime()) {
          dateText = 'Today';
      } else if (lessonDateOnly.getTime() === tomorrowOnly.getTime()) {
          dateText = 'Tomorrow';
        } else {
          dateText = lessonDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
        }
        
        return {
          date: dateText,
        time: nextLesson.timeFrom || nextLesson.from || 'TBD',
          fullDate: lessonDate
        };
    } catch (error) {
      console.error('Error finding next lesson:', error);
      return null;
    }
  };

  // Update schedule and teacher info when schedule changes
  useEffect(() => {
    console.log('HomeStudent useEffect - schedule state changed:', schedule);
    console.log('HomeStudent useEffect - schedule length:', schedule?.length);
    
    if (schedule && schedule.length > 0) {
      const sortedSchedule = getTeacherSchedule();
      const next = getNextLesson();
      console.log('HomeStudent useEffect - sortedSchedule:', sortedSchedule);
      console.log('HomeStudent useEffect - nextLesson:', next);
      
      setTeacherSchedule(sortedSchedule);
      setNextLesson(next);
    } else {
      console.log('HomeStudent useEffect - No schedule available');
      setTeacherSchedule([]);
      setNextLesson(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  // Current week days for Upcoming lessons strip (Mon–Sun with hasLesson)
  const weekStrip = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    const weekDates = days.map((_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
    const scheduleDateStrings = (teacherSchedule || []).map(item => {
      if (!item.date) return null;
      const d = typeof item.date === 'string' && item.date.match(/^\d{4}-\d{2}-\d{2}$/)
        ? new Date(item.date + 'T00:00:00')
        : new Date(item.date);
      return d.toDateString();
    }).filter(Boolean);
    const todayStr = today.toDateString();
    const todayMs = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    return days.map((label, i) => {
      const d = weekDates[i];
      const dateStr = d.toDateString();
      const hasLesson = scheduleDateStrings.some(s => s === dateStr);
      const isToday = dateStr === todayStr;
      const dMs = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const lessonPast = hasLesson && dMs < todayMs;
      const lessonFuture = hasLesson && dMs > todayMs;
      return { label, date: d.getDate(), hasLesson, dateObj: d, isToday, lessonPast, lessonFuture };
    });
  }, [teacherSchedule]);

  const lessonsOnSelectedDay = useMemo(() => {
    if (selectedWeekDayIndex == null || !teacherSchedule.length) return [];
    const dayInfo = weekStrip[selectedWeekDayIndex];
    if (!dayInfo?.dateObj) return [];
    const targetDateStr = dayInfo.dateObj.toDateString();
    return teacherSchedule.filter((item) => {
      if (!item.date) return false;
      const d = typeof item.date === 'string' && item.date.match(/^\d{4}-\d{2}-\d{2}$/)
        ? new Date(item.date + 'T00:00:00')
        : new Date(item.date);
      return d.toDateString() === targetDateStr;
    });
  }, [teacherSchedule, weekStrip, selectedWeekDayIndex]);

  const renderLessonsSection = () => {
    console.log('=== renderLessonsSection DEBUG ===');
    console.log('filteredLessons length:', filteredLessons.length);
    console.log('filteredLessons:', filteredLessons);
    console.log('First lesson details:', filteredLessons[0]);
    console.log('Lesson title:', filteredLessons[0]?.title);
    console.log('Lesson date:', filteredLessons[0]?.date);
    console.log('Lesson from:', filteredLessons[0]?.from);
    
    return (
    <div className="lessons-section-content" data-tour="student-home-lessons">
      <div className="lessons-header">
        <div className="lessons-search-bar">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
            <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
          </svg>
          <input
            type="text"
            placeholder="Search lessons..."
            value={lessonSearchTerm}
            onChange={(e) => setLessonSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="lessons-table-container">
        <table>
          <thead>
            <tr>
              <th>Lesson Name</th>
              <th>Date</th>
              <th>Time</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {lessonsLoading ? (
              <tr>
                <td colSpan="5" className="lessons-loading-cell">
                  <span className="lessons-loading-spinner" aria-hidden />
                  <span>Loading lessons…</span>
                </td>
              </tr>
            ) : filteredLessons.length > 0 ? (
              filteredLessons.map((lesson) => (
                <tr key={lesson.id} className={`lesson-row lesson-row-status-${lesson.status || 'pending'}`}>
                  <td>
                    <div className="lesson-name-cell">
                      <span 
                        className="lesson-name clickable-lesson-name"
                        onClick={() => {
                          console.log('=== LESSON TITLE CLICK DEBUG ===');
                          console.log('onLessonClick function:', onLessonClick);
                          console.log('lesson.id:', lesson.id);
                          console.log('lesson object:', lesson);
                          
                          if (onLessonClick) {
                            console.log('Calling onLessonClick with lesson title:', lesson.id);
                            onLessonClick(lesson.id);
                          } else {
                            console.log('onLessonClick function not available');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {lesson.title}
                      </span>
                    </div>
                  </td>
                  <td className="lesson-date-cell">{lesson.date}</td>
                  <td className="lesson-time-cell">{formatLessonTime(lesson)}</td>
                  <td>
                    <span className={`lesson-status lesson-status-${lesson.status}`}>
                      {lesson.status === 'in_progress' && 'In Progress'}
                      {lesson.status === 'done' && 'Done'}
                      {lesson.status === 'cancelled' && 'Cancelled'}
                      {lesson.status === 'pending' && 'Pending'}
                    </span>
                  </td>
                  <td>
                    <div className="lesson-actions">
                      <button 
                        className={`enter-class-btn ${canEnterLesson(lesson) ? 'active' : 'disabled'}`}
                        disabled={!canEnterLesson(lesson)}
                        title={canEnterLesson(lesson) ? 'Enter the class' : 'Available 10 minutes before lesson starts'}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => {
                          console.log('=== LESSON CLICK DEBUG ===');
                          console.log('onLessonClick function:', onLessonClick);
                          console.log('canEnterLesson(lesson):', canEnterLesson(lesson));
                          console.log('lesson.id:', lesson.id);
                          console.log('lesson object:', lesson);
                          
                          if (onLessonClick && canEnterLesson(lesson)) {
                            console.log('Calling onLessonClick with:', lesson.id);
                            onLessonClick(lesson.id);
                          } else {
                            console.log('Cannot call onLessonClick - function or condition not met');
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" fill="currentColor"/>
                        </svg>
                        {canEnterLesson(lesson) ? 'Enter the class' : 'Not available'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-lessons-message">No lessons found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    );
  };


  return (
    <div className="student-home-page">
      
      <div className="student-home-banner" data-tour="student-home-banner">
        <div className="student-home-banner-top-left-actions">
          {/* Back button removed */}
        </div>

        <div className="student-home-banner-content">
          <h2 className="student-home-student-name-banner">{studentData?.name || 'Student'}</h2>
          <p className="student-home-student-proficiency">{studentData?.proficiency || 'Beginner'}</p>
        </div>

        <div className="student-home-banner-stats">
          {(() => {
            // Reconciled via usePaymentBalance (same logic the teacher's
            // StudentProfile uses) — reading studentData.payments directly
            // here showed a stale number whenever the teacher hadn't
            // opened this student's profile since their last completed
            // lesson. Sign reflects direction: positive = paid ahead,
            // negative = owed.
            const balance = paymentBalance;
            const currency = paymentCurrency;
            const isPositive = balance >= 0;
            // "Completed lessons" here used to be studentData.lessons — an
            // array this page's own studentData never actually populates,
            // so it always read 0/0. Same lesson-count-from-balance figure
            // as StudentProfileSettings.jsx's Payments tab instead: how
            // many lessons unpaid, or paid ahead if there's a credit.
            const priceRaw = studentData?.price || '';
            const priceMatch = priceRaw.match(/(\d+(?:\.\d+)?)/);
            const pricePerHour = priceMatch ? parseFloat(priceMatch[1]) : 0;
            const lessonsCount = pricePerHour > 0 ? Math.round(Math.abs(balance) / pricePerHour) : 0;
            return (
              <>
                <div className="student-home-banner-stat" data-tour="student-home-stat-completed">
                  <span className="student-home-banner-stat-value">
                    {lessonsCount}
                  </span>
                  <span className="student-home-banner-stat-label">
                    {isPositive ? 'lessons paid ahead' : 'lessons unpaid'}
                  </span>
                </div>
                <div className="student-home-banner-stat-divider" />
                <div className={`student-home-banner-stat student-home-banner-balance ${isPositive ? 'positive' : 'negative'}`}>
                  <span className="student-home-banner-stat-value">
                    {balance < 0 ? '−' : ''}{currency}{Math.abs(balance).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </span>
                  <span className="student-home-banner-stat-label">
                    {isPositive ? 'Balance' : 'Debt'}
                  </span>
                </div>
                <div className="student-home-banner-stat-divider" />
              </>
            );
          })()}
          <div className="student-home-banner-stat" data-tour="student-home-pay">
            <button type="button" className="student-home-banner-pay-btn" onClick={() => { setPayToast('This feature is under development.'); setTimeout(() => setPayToast(null), 3500); }}>Pay</button>
          </div>
        </div>
        
        <div className="student-home-novas-banner-logo">
          <img src={novasLogo} alt="Novas Logo" />
        </div>
      </div>
      
      {/* Teacher Info, Stats and Schedule Row */}
      <div className="teacher-stats-schedule-row">
        {/* Teacher Info Card - styled like reference image */}
        <div className="teacher-info-card" data-tour="student-home-teacher">
          <div className="teacher-card-banner">
            <video
              className="teacher-card-banner-video"
              src="/banner-video.mp4"
              autoPlay
              loop
              muted
              playsInline
              aria-hidden
            />
          </div>
          <div className="teacher-card-avatar">
            {(() => {
              const teacherInfo = getTeacherContactInfo();
              return teacherInfo.photo ? (
                <img src={teacherInfo.photo} alt="Teacher" className="teacher-card-photo" />
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" className="teacher-card-placeholder">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              );
            })()}
          </div>
          <div className="teacher-card-body">
            <div className="teacher-card-name">{getTeacherContactInfo().name}</div>
            <div className="teacher-card-role">Your Teacher</div>
            <div className="teacher-card-contact">
              <div className="teacher-contact-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{getTeacherContactInfo().email || '—'}</span>
              </div>
              {getTeacherContactInfo().phone && (
                <div className="teacher-contact-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{getTeacherContactInfo().phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Homework + Dictionary stacked to the right of teacher card */}
        <div className="student-home-widgets-column">
          <div className="student-home-widget homework-widget" data-tour="student-home-homework-widget" onClick={() => onGoToHomework?.()}>
            <div className="homework-widget-icon" aria-hidden>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="homework-widget-text">
              <span className="homework-widget-label">Homework</span>
              {homeworkLoading ? (
                <span className="homework-widget-meta">Loading…</span>
              ) : (
                <span className="homework-widget-meta">
                  {homeworkSummary.new > 0 && `${homeworkSummary.new} new`}
                  {homeworkSummary.new > 0 && homeworkSummary.toReview > 0 && ' · '}
                  {homeworkSummary.toReview > 0 && `${homeworkSummary.toReview} to review`}
                  {homeworkSummary.new === 0 && homeworkSummary.toReview === 0 && 'No new assignments'}
                </span>
              )}
            </div>
            <button type="button" className="homework-widget-view-btn" onClick={(e) => { e.stopPropagation(); onGoToHomework?.(); }}>View</button>
          </div>
          <div className="dictionary-widget dictionary-widget--bare" data-tour="student-home-dictionary-widget" onClick={() => onGoToDictionary?.()}>
            <div className="dictionary-widget-header">
              <span className="dictionary-widget-title">Last added to dictionary</span>
              <button type="button" className="dictionary-widget-view-link" onClick={(e) => { e.stopPropagation(); onGoToDictionary?.(); }}>View all</button>
            </div>
            {dictionaryLoading ? (
              <p className="dictionary-widget-loading">Loading…</p>
            ) : dictionaryDeck.length > 0 ? (
              <div
                className={`dictionary-deck ${deckBusy ? 'is-busy' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (deckBusy) return;
                  const now = Date.now();
                  if (now - lastDeckTapRef.current < 400) return; // один тап = один flip (touch + click не дублируем)
                  lastDeckTapRef.current = now;
                  setFlippedCardId(prev => dictionaryDeck[0]?.id === prev ? null : dictionaryDeck[0]?.id);
                }}
              >
                <div className="dictionary-deck-stack dictionary-deck-stack--2" aria-hidden />
                <div className="dictionary-deck-stack dictionary-deck-stack--1" aria-hidden />

                <div className={`dictionary-flashcard-motion ${deckMotion ? `is-${deckMotion}` : ''}`}>
                  <div className={`dictionary-flashcard ${dictionaryDeck[0]?.id === flippedCardId ? 'is-flipped' : ''}`}>
                    <div className="dictionary-flashcard-inner">
                      <div className="dictionary-flashcard-face dictionary-flashcard-front">
                        <div className="dictionary-flashcard-main">
                          <div className="dictionary-flashcard-word-row">
                            <span className="dictionary-flashcard-word">{dictionaryDeck[0].word || '—'}</span>
                          </div>
                          {(dictionaryDeck[0].phonetic || dictionaryDeck[0].pronunciation) && (
                            <div className="dictionary-flashcard-phonetic">
                              {dictionaryDeck[0].phonetic || dictionaryDeck[0].pronunciation}
                            </div>
                          )}
                          <div className="dictionary-flashcard-hint">Tap to reveal</div>
                        </div>
                        <button
                          type="button"
                          className="dictionary-flashcard-audio"
                          aria-label="Play pronunciation"
                          onClick={(e) => { e.stopPropagation(); if (!deckBusy) speakWord(dictionaryDeck?.[0]?.word); }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M11 5L7 9H4v6h3l4 4V5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                            <path d="M15 9a4 4 0 0 1 0 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M17.5 6.5a7 7 0 0 1 0 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>

                      <div className="dictionary-flashcard-face dictionary-flashcard-back">
                        <div className="dictionary-flashcard-back-main">
                          <div className="dictionary-flashcard-back-title">Meaning</div>
                          <div className="dictionary-flashcard-meaning">
                            {dictionaryDeck[0].translation || '—'}
                          </div>
                        </div>
                        <div className="dictionary-flashcard-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="dictionary-flashcard-btn dictionary-flashcard-btn--dont"
                            onClick={handleDeckDontKnow}
                            disabled={deckBusy}
                          >
                            Don’t know
                          </button>
                          <button
                            type="button"
                            className="dictionary-flashcard-btn dictionary-flashcard-btn--know"
                            onClick={handleDeckKnow}
                            disabled={deckBusy}
                          >
                            Know
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="dictionary-deck-finished" onClick={(e) => e.stopPropagation()}>
                <div className="dictionary-confetti" aria-hidden>
                  <span className="confetti c1" />
                  <span className="confetti c2" />
                  <span className="confetti c3" />
                  <span className="confetti c4" />
                  <span className="confetti c5" />
                  <span className="confetti c6" />
                  <span className="confetti c7" />
                  <span className="confetti c8" />
                </div>
                <div className="dictionary-deck-finished-title">Nice!</div>
                <div className="dictionary-deck-finished-subtitle">You know all these words.</div>
                <div className="dictionary-deck-finished-actions">
                  <button type="button" className="dictionary-deck-finished-btn" onClick={loadMoreDeckWords}>
                    More words
                  </button>
                  <button type="button" className="dictionary-deck-finished-btn dictionary-deck-finished-btn--secondary" onClick={resetLearnedWords}>
                    Repeat
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Lessons - narrower */}
        <div className="info-container teacher-schedule-container upcoming-lessons-card" data-tour="student-home-schedule">
          <div className="upcoming-lessons-header">
            <h3 className="upcoming-lessons-title">Class schedule</h3>
          </div>
          {teacherSchedule.length > 0 ? (
            <>
              <div className="upcoming-lessons-week-strip">
                {weekStrip.map((day, i) => (
                  <div
                    key={day.label}
                    role="button"
                    tabIndex={0}
                    className={`upcoming-lessons-day ${day.hasLesson ? 'has-lesson' : ''} ${day.lessonPast ? 'lesson-past' : ''} ${day.lessonFuture ? 'lesson-future' : ''} ${selectedWeekDayIndex === i ? 'selected' : ''} ${day.isToday ? 'is-today' : ''}`}
                    onClick={() => setSelectedWeekDayIndex(i)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedWeekDayIndex(i); } }}
                    aria-pressed={selectedWeekDayIndex === i}
                    aria-label={`${day.label} ${day.date}${day.isToday ? ', today' : ''}${day.hasLesson ? ', has lessons' : ''}`}
                  >
                    <span className="upcoming-lessons-day-circle" aria-hidden />
                    <span className="upcoming-lessons-day-label">{day.label}</span>
                    <span className="upcoming-lessons-day-num">{day.date}</span>
                  </div>
                ))}
              </div>
              <div className="upcoming-lessons-timeline">
                {selectedWeekDayIndex == null ? (
                  <p className="upcoming-lessons-pick-day">Click a day to see lessons</p>
                ) : lessonsOnSelectedDay.length === 0 ? (
                  <p className="upcoming-lessons-no-lessons-day">No lessons on this day</p>
                ) : (
                  lessonsOnSelectedDay.map((item, index) => {
                    const formatted = formatLessonDateTime(item);
                    const title = item.title && !item.title.includes('Unknown Student') ? item.title : 'Lesson';
                    const teacherTz = studentData?.teacher?.timezone || null;
                    const studentTz = studentData?.timezone || null;
                    const timeStr = convertTimeRange({
                      date: item.date,
                      from: item.timeFrom || item.from,
                      to: item.timeTo || item.to
                    }, teacherTz, studentTz) || formatted.time || (item.timeFrom || item.from) || '—';
                    const status = getLessonStatus(item);
                    const statusLabel = status === 'upcoming' ? 'upcoming' : status === 'in-progress' ? 'in progress' : 'completed';
                    const shortDate = formatScheduleCardDate(item);
                    return (
                      <div
                        key={item.id || index}
                        className={`schedule-lesson-card schedule-lesson-card--${status}`}
                        aria-label={`${title}, ${shortDate}, ${timeStr}, ${statusLabel}`}
                      >
                        <div className="schedule-lesson-left">
                          <span className="schedule-lesson-time">{timeStr}</span>
                          <span className="schedule-lesson-divider" aria-hidden />
                          <span className="schedule-lesson-date">{shortDate}</span>
                        </div>

                        <span className={`schedule-lesson-status schedule-lesson-status--${status}`}>
                          <span className={`schedule-lesson-status-icon schedule-lesson-status-icon--${status}`} aria-hidden />
                          {statusLabel}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="no-schedule-friendly">
              <div className="no-schedule-icon">📅</div>
              <p>No upcoming lessons</p>
            </div>
          )}
        </div>
      </div>

      {/* Lessons Section */}
      <div className="lessons-dictionary-materials-section">
        <div className="section-tabs-navigation">
          <button
            className={`section-tab-button ${activeSectionTab === 'Lessons' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Lessons')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 19.5C4 18.1193 5.11929 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.5 2H20V22H6.5C5.11929 22 4 20.8807 4 19.5V4.5C4 3.11929 5.11929 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Lessons
          </button>
        </div>

        <div className="section-content">
          {activeSectionTab === 'Lessons' && renderLessonsSection()}
        </div>
      </div>

      {payToast && (
        <div className="student-home-pay-toast" role="status" aria-live="polite">
          <div className="student-home-pay-toast-inner">
            <span className="student-home-pay-toast-icon" aria-hidden>ℹ</span>
            <p className="student-home-pay-toast-message">{payToast}</p>
            <button type="button" className="student-home-pay-toast-close" onClick={() => setPayToast(null)} aria-label="Close">×</button>
          </div>
        </div>
      )}
      
    </div>
  );
}

